import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { MenuOptionValue, Paginated, PaginationQuery } from "../../types";
import useApi, { buildUrl } from "../api";
import { CustomError } from "../errors";
import { ProcessingType, UpsertProcessingType } from "./type";

const useProcessingTypeApi = () => {
  const baseurl = "/processing_types";

  const api = useApi();

  const searchProcessingTypes = useCallback(
    async (query: PaginationQuery, signal?: AbortSignal) => {
      const url = buildUrl(baseurl, query);
      const data = await api.get<Paginated<ProcessingType>>({ url, signal });
      return data.items;
    },
    [api]
  );

  const useProcessingTypes = useCallback(
    (query: PaginationQuery) => {
      const url = buildUrl(baseurl, query);
      const result = useQuery<Paginated<ProcessingType>, CustomError>({
        queryKey: [url],
        queryFn: ({ signal }) => api.get<Paginated<ProcessingType>>({ url, signal }),
      });
      return { ...result, error: result.error ?? undefined };
    },
    [api]
  );

  const createProcessingType = useCallback(
    async (pt: UpsertProcessingType) => {
      const data = await api.post<UpsertProcessingType, ProcessingType>(baseurl, pt);
      api.mutateCache([baseurl]);
      return data;
    },
    [api]
  );

  const updateProcessingType = useCallback(
    async (pt: UpsertProcessingType, typeID: string) => {
      const data = await api.put<UpsertProcessingType>(`${baseurl}/${typeID}`, pt);
      api.mutateCache([baseurl]);
      return data;
    },
    [api]
  );

  const deleteProcessingType = useCallback(
    async (typeID: string) => {
      const data = await api.del(`${baseurl}/${typeID}`);
      api.mutateCache([baseurl]);
      return data;
    },
    [api]
  );

  const getFieldFiltersQuery = useCallback(
    (fieldName: string) => {
      const url = `${baseurl}/filters?field=${fieldName}`;
      return {
        queryKey: [url],
        queryFn: ({ signal }: { signal: AbortSignal }) => api.get<MenuOptionValue[]>({ url, signal }),
      };
    },
    [api]
  );

  return {
    searchProcessingTypes,
    useProcessingTypes,
    createProcessingType,
    updateProcessingType,
    deleteProcessingType,
    getFieldFiltersQuery,
  };
};

export default useProcessingTypeApi;
